// @flow
import React, {
  type Node,
  type StatelessFunctionalComponent,
} from 'react';

import type {
  GroupByType,
  Issue,
} from 'chronos-types';

import ArrowUpIcon from '@atlaskit/icon/glyph/arrow-up';
import ArrowDownIcon from '@atlaskit/icon/glyph/arrow-down';
import Button from '@atlaskit/button';
import RefinementBar,
{
  SearchFilter,
} from '@atlaskit/refinement-bar';
import DropdownMenu, {
  DropdownItemGroupRadio,
  DropdownItemRadio,
} from '@atlaskit/dropdown-menu';


import SidebarList from './SidebarList';
import SidebarFooter from './SidebarFooter';
import SidebarShowButton from './SidebarShowButton';

import * as S from './styled';

type Props = {
  /**
   * Array of items which were logged
   * */
  data: Array<Issue>,
  /**
   * Set true or false while receive data
   * */
  fetching: boolean,
  /**
   * Set visibility of sidebar
   * */
  isSidebarVisible: boolean,
  /**
   * Set true or false to sidebar visibility
   * */
  setSidebarVisible: (value: boolean) => void,
  /**
   * Whether groupBy popup is open
   * */
  isGroupByOpen: boolean,
  /**
   * Set groupBy one of issues, epics, users, projects, versions.
   * */
  setGroupByVisible: (value: boolean) => void,
  /**
   * Set groupBy one of issues, epics, users, projects, versions.
   * */
  onGroupBy: (type: GroupByType) => void,
  /**
   * groupBy one of issues, epics, users, projects, versions.
   * */
  groupBy: GroupByType,
  /**
   * Function that is called after click selected count
   * */
  onClearSelectedItems: { (e: any): void },
  /**
   * Function that is called after click clear all button
   * */
  onClearAllSelectedItems: { (e: any): void },
  /**
   * Array of selected issues.
   * */
  selected: { [GroupByType]: Array<string> },
  /**
   * Function that is called after issue selected.
   * */
  onSelect: { (index: number): void },
  /**
   * The value of the input.
   * */
  search: string,
  /**
   * Handler to be called when the input changes.
   * */
  onSearchChange: { (value: string): void },
  /**
   * Handler to be called when click on clear icon.
   * */
  /**
   * Set position of the button(true = 'static', false='absolute').
   * */
  isAbsolute: boolean,
};

const groupByItems = [
  'issues',
  'users',
  'projects',
];

const orderByItems = [
  'Created',
  'Key',
  'Last viewed',
  'Priority',
  'Resolved',
  'Status',
  'Updated',
];

const Sidebar: StatelessFunctionalComponent<Props> = ({
  data,
  groupBy,
  orderBy,
  orderByType,
  onGroupBy,
  onOrderBy,
  isSidebarVisible,
  setSidebarVisible,
  fetching,
  selected,
  onSelect,
  search,
  onSearchChange,
  isAbsolute,
}: Props): Node => {
  return (
    <S.Sidebar visible={isSidebarVisible}>
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '10px',
      }}
      >
        <RefinementBar
          fieldConfig={{
            search: {
              label: 'Search',
              type: SearchFilter,
            },
          }}
          irremovableKeys={['search']}
          onChange={e => onSearchChange(e.search)}
          value={{
            search,
          }}
        />
        <div style={{ marginLeft: '10px' }}>
          <DropdownMenu
            trigger={groupBy}
            triggerType="button"
          >
            <DropdownItemGroupRadio
              id="group-issues"
              title="GROUP WORKLOGS BY"
            >
              {groupByItems.map(item => (
                <DropdownItemRadio
                  key={item}
                  id={item}
                  isSelected={groupBy === item}
                  onClick={() => onGroupBy(item)}
                >
                  {item}
                </DropdownItemRadio>
              ))}
            </DropdownItemGroupRadio>
          </DropdownMenu>
        </div>
      </div>

      {
        groupBy === 'issues'
          && (
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: '10px',
            }}
            >
              <DropdownMenu
                trigger={orderBy}
                triggerType="button"
              >
                <DropdownItemGroupRadio
                  id="order-issues"
                  title="ORDER ISSUES BY"
                >
                  {orderByItems.map(item => (
                    <DropdownItemRadio
                      key={item}
                      id={item}
                      isSelected={orderBy === item}
                      onClick={() => onOrderBy({
                        orderBy: item,
                        orderByType,
                      })}
                    >
                      {item}
                    </DropdownItemRadio>
                  ))}
                </DropdownItemGroupRadio>
              </DropdownMenu>
              <Button
                appearance="subtle"
                style={{
                  marginLeft: '10px',
                }}
                onClick={() => {
                  onOrderBy({
                    orderBy,
                    orderByType: (
                      orderByType === 'ascending'
                        ? 'descending'
                        : 'ascending'
                    ),
                  });
                }}
                iconAfter={(
                  orderByType === 'ascending'
                    ? <ArrowDownIcon label="Test icon" size="small" />
                    : <ArrowUpIcon label="Test icon" size="small" />
                )}
              />
            </div>
          )
      }
      <SidebarList
        data={data}
        groupBy={groupBy}
        fetching={fetching}
        selected={selected}
        onSelect={onSelect}
      />
      <SidebarFooter onClick={() => setSidebarVisible(false)} />
      <SidebarShowButton
        onClick={() => setSidebarVisible(true)}
        isAbsolute={isAbsolute}
        visible={!isSidebarVisible}
      />
    </S.Sidebar>
  );
};

export default Sidebar;
